import { Columns3 } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface ColDef<K extends string = string> {
  key: K;
  label: string;
  defaultVisible?: boolean;
  /** Group label shown as section header in the picker */
  group?: string;
}

// ── Hook ───────────────────────────────────────────────────────────────────────

export function useColumnVisibility<K extends string>(cols: ColDef<K>[], storageKey?: string, initialMap?: Record<K, boolean>) {
  const [visible, setVisible] = useState<Record<K, boolean>>(() => {
    if (storageKey) {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        try {
          const parsed = JSON.parse(stored) as Record<K, boolean>;
          const defaultMap = Object.fromEntries(cols.map((c) => [c.key, c.defaultVisible ?? true])) as Record<K, boolean>;
          return { ...defaultMap, ...parsed };
        } catch {
          // ignore
        }
      }
    }
    return initialMap || (Object.fromEntries(cols.map((c) => [c.key, c.defaultVisible ?? true])) as Record<K, boolean>);
  });

  // Re-inicializar cuando cambian las columnas o la clave de almacenamiento
  useEffect(() => {
    if (storageKey) {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        try {
          const parsed = JSON.parse(stored) as Record<K, boolean>;
          const defaultMap = Object.fromEntries(cols.map((c) => [c.key, c.defaultVisible ?? true])) as Record<K, boolean>;
          setVisible({ ...defaultMap, ...parsed });
          return;
        } catch {
          // ignore
        }
      }
    }
    const defaultMap = Object.fromEntries(cols.map((c) => [c.key, c.defaultVisible ?? true])) as Record<K, boolean>;
    setVisible(initialMap || defaultMap);
  }, [storageKey, cols, initialMap]);

  function toggle(key: K) {
    setVisible((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      if (Object.values(next).some(Boolean)) {
        if (storageKey) {
          localStorage.setItem(storageKey, JSON.stringify(next));
        }
        return next;
      }
      return prev;
    });
  }

  function reset() {
    const defaultMap = Object.fromEntries(cols.map((c) => [c.key, c.defaultVisible ?? true])) as Record<K, boolean>;
    setVisible(defaultMap);
    if (storageKey) {
      localStorage.removeItem(storageKey);
    }
  }

  function selectAll() {
    const next = Object.fromEntries(cols.map((c) => [c.key, true])) as Record<K, boolean>;
    setVisible(next);
    if (storageKey) {
      localStorage.setItem(storageKey, JSON.stringify(next));
    }
  }

  function toggleGroup(groupCols: ColDef<K>[]) {
    setVisible((prev) => {
      const allVisible = groupCols.every((c) => prev[c.key]);
      const next = { ...prev };
      for (const c of groupCols) next[c.key] = !allVisible;
      if (!Object.values(next).some(Boolean)) return prev;
      if (storageKey) {
        localStorage.setItem(storageKey, JSON.stringify(next));
      }
      return next;
    });
  }

  /** Register new keys as hidden (for dynamically-derived cols) */
  function registerKeys(newKeys: K[]) {
    setVisible((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const k of newKeys) {
        if (!(k in next)) {
          next[k] = false;
          changed = true;
        }
      }
      if (changed && storageKey) {
        localStorage.setItem(storageKey, JSON.stringify(next));
      }
      return changed ? next : prev;
    });
  }

  const activeCount = (Object.values(visible) as boolean[]).filter(Boolean).length;

  return { visible, setVisible, toggle, reset, selectAll, toggleGroup, registerKeys, activeCount };
}

// ── ColumnPicker component ─────────────────────────────────────────────────────

interface Props<K extends string> {
  cols: ColDef<K>[];
  visible: Record<K, boolean>;
  onToggle: (key: K) => void;
  onReset: () => void;
  onSelectAll?: () => void;
  onToggleGroup?: (groupCols: ColDef<K>[]) => void;
  activeCount: number;
}

export function ColumnPicker<K extends string>({ cols, visible, onToggle, onReset, onSelectAll, onToggleGroup, activeCount }: Props<K>) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const [pos, setPos] = useState({ top: 0, left: 0, placement: 'bottom' as 'top' | 'bottom', maxHeight: 320 });

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;

    const updatePosition = () => {
      if (!btnRef.current) return;
      const rect = btnRef.current.getBoundingClientRect();
      const DROPDOWN_WIDTH = 220;
      const GAP = 6;
      const MARGIN = 8;
      const MAX_HEIGHT_CAP = Math.floor(window.innerHeight * 0.6);
      const windowHeight = window.innerHeight;
      const windowWidth = window.innerWidth;

      // Horizontal: align right edge of dropdown with right edge of button, clamp to viewport
      let left = rect.right - DROPDOWN_WIDTH;
      left = Math.max(MARGIN, Math.min(left, windowWidth - DROPDOWN_WIDTH - MARGIN));

      // Vertical: measure real available space in each direction
      const spaceBelow = windowHeight - rect.bottom - GAP - MARGIN;
      const spaceAbove = rect.top - GAP - MARGIN;
      const shouldOpenAbove = spaceBelow < 200 && spaceAbove > spaceBelow;

      // maxHeight is the lesser of available space and the global cap
      const maxHeight = Math.max(80, Math.min(shouldOpenAbove ? spaceAbove : spaceBelow, MAX_HEIGHT_CAP));

      setPos({
        top: shouldOpenAbove ? rect.top - GAP : rect.bottom + GAP,
        left,
        placement: shouldOpenAbove ? 'top' : 'bottom',
        maxHeight,
      });
    };

    updatePosition();
    const timeout = setTimeout(updatePosition, 0);
    return () => clearTimeout(timeout);
  }, [open]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (btnRef.current && !btnRef.current.contains(target) && menuRef.current && !menuRef.current.contains(target)) {
        setOpen(false);
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', handleClick);
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    function handleScroll(e: Event) {
      if (menuRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    }
    function handleResize() {
      setOpen(false);
    }

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [open]);

  const grouped = new Map<string | undefined, ColDef<K>[]>();
  for (const col of cols) {
    const g = col.group;
    if (!grouped.has(g)) grouped.set(g, []);
    grouped.get(g)!.push(col);
  }

  return (
    <div className="shrink-0 flex items-center">
      <button
        ref={btnRef}
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center gap-1.5 text-[11px] px-2.5 py-1.5 rounded-lg border transition-all font-medium ${
          open
            ? 'bg-(--admin-blue-bg) text-(--admin-blue) border-(--admin-blue)'
            : 'bg-(--admin-surface2) text-(--admin-text2) border-(--admin-border) hover:border-(--admin-blue) hover:text-(--admin-blue)'
        }`}
        title="Mostrar / ocultar columnas"
      >
        <Columns3 className="w-3.5 h-3.5" />
        Columnas
        <span
          className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[9px] font-bold leading-none ${
            open ? 'bg-(--admin-blue) text-white' : 'bg-(--admin-border2) text-(--admin-text2)'
          }`}
        >
          {activeCount}
        </span>
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: 'fixed',
              top: pos.top,
              left: pos.left,
              transform: pos.placement === 'top' ? 'translateY(-100%)' : 'none',
              zIndex: 999999,
              maxHeight: pos.maxHeight,
              animation: pos.placement === 'top' ? 'fadeInUp 0.12s ease' : 'fadeInDown 0.12s ease',
            }}
            className="bg-white border border-(--admin-border) rounded-xl shadow-2xl min-w-52 pointer-events-auto flex flex-col"
          >
            <div className="px-3 py-1.5 border-b border-(--admin-border) flex items-center justify-between shrink-0">
              <span className="text-[11px] font-semibold text-(--admin-text2) uppercase tracking-wider">Columnas</span>
              <div className="flex items-center gap-2">
                {onSelectAll && (
                  <button onClick={onSelectAll} className="text-[10px] text-(--admin-blue) hover:underline font-medium">
                    Todas
                  </button>
                )}
                <button onClick={onReset} className="text-[10px] text-(--admin-text3) hover:underline font-medium">
                  Restablecer
                </button>
              </div>
            </div>
            <div className="py-1 overflow-y-auto scrollbar-thin min-h-0">
              {Array.from(grouped.entries()).map(([group, groupCols]) => {
                const allGroupVisible = groupCols.every((c) => visible[c.key]);
                return (
                  <div key={group ?? '__default__'}>
                    {group && (
                      <div className="px-3 pt-2 pb-0.5 flex items-center justify-between">
                        <span className="text-[10px] font-bold text-(--admin-text3) uppercase tracking-wider">{group}</span>
                        {onToggleGroup && (
                          <button
                            onClick={() => onToggleGroup(groupCols)}
                            className="text-[9px] text-(--admin-blue) hover:underline font-medium leading-none"
                          >
                            {allGroupVisible ? 'Quitar' : 'Todos'}
                          </button>
                        )}
                      </div>
                    )}
                    {groupCols.map((col) => {
                      const checked = !!visible[col.key];
                      return (
                        <label
                          key={col.key}
                          className="flex items-center gap-2.5 px-3 py-1.5 cursor-pointer hover:bg-(--admin-surface2) transition-colors group select-none"
                        >
                          <span
                            className={`w-4 h-4 rounded flex items-center justify-center border transition-all shrink-0 ${
                              checked
                                ? 'bg-(--admin-blue) border-(--admin-blue)'
                                : 'bg-white border-(--admin-border2) group-hover:border-(--admin-blue)'
                            }`}
                          >
                            {checked && (
                              <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 8" fill="none">
                                <path
                                  d="M1 4l3 3 5-6"
                                  stroke="currentColor"
                                  strokeWidth="1.5"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            )}
                          </span>
                          <input type="checkbox" className="sr-only" checked={checked} onChange={() => onToggle(col.key)} />
                          <span className={`text-[12px] flex-1 ${checked ? 'text-(--admin-text) font-medium' : 'text-(--admin-text3)'}`}>
                            {col.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
